// src/components/hooks/useCitas.ts

import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import type { RawAppointment } from '@/components/types/typeCalendar';

/* ============================================================
   HOOKS DE LECTURA
============================================================ */

export const useCitasDetallesPorEmpresa = (
  startDate: string,
  endDate: string,
  activeStates: string[]
) => {
  const [appointments, setAppointments] = useState<RawAppointment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAppointments = async () => {
    const idEmpresa = localStorage.getItem('id_empresa');
    if (!idEmpresa) return;

    setLoading(true);

    let query = supabase
      .from('vista_citas_detalle')
      .select('*')
      .eq('id_empresa', idEmpresa);

    if (startDate) query = query.gte('fecha_cita', startDate);
    if (endDate) query = query.lte('fecha_cita', endDate);
    if (activeStates.length) query = query.in('estado_cita', activeStates);

    const { data, error } = await query
      .order('fecha_cita', { ascending: false })
      .order('hora_cita', { ascending: false });

    if (!error && data) setAppointments(data as RawAppointment[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAppointments();
  }, [startDate, endDate, activeStates]);

  return { appointments, loading, fetchAppointments };
};

export const useCitasDetallesPorUsuario = (
  idEmpresa: string | null,
  idUsuario: string | null,
  startDate: string,
  endDate: string,
  activeStates: string[]
) => {
  const [appointments, setAppointments] = useState<RawAppointment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAppointments = async () => {
    if (!idUsuario || !idEmpresa) return;
    setLoading(true);

    let query = supabase
      .from('vista_citas_detalle')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .contains(
        'trabajadores_en_cita',
        JSON.stringify([{ id_usuario: idUsuario }])
      );

    if (startDate) query = query.gte('fecha_cita', startDate);
    if (endDate) query = query.lte('fecha_cita', endDate);
    if (activeStates.length) query = query.in('estado_cita', activeStates);

    const { data, error } = await query
      .order('fecha_cita', { ascending: false })
      .order('hora_cita', { ascending: false });

    if (!error && data) setAppointments(data as RawAppointment[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAppointments();
  }, [idEmpresa, idUsuario, startDate, endDate, activeStates]);

  return { appointments, loading, fetchAppointments };
};

export const useCitasPorCliente = (email: string | null) => {
  const [appointments, setAppointments] = useState<RawAppointment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAppointments = async () => {
    if (!email) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('vista_citas_detalle')
      .select('*')
      .eq('email_contacto', email)
      .order('fecha_cita', { ascending: false })
      .order('hora_cita', { ascending: false });

    if (!error && data) setAppointments(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAppointments();
  }, [email]);

  return { appointments, loading, fetchAppointments };
};

/* ============================================================
   CITAS PENDIENTES DEL CLIENTE
   Devuelve la lista además del booleano, para poder avisar de
   solapes al reservar (no solo de que existan citas).
============================================================ */

export interface CitaPendienteCliente {
  id_cita: string;
  fecha_cita: string;
  hora_cita: string;
  hora_fin: string;
  nombre_empresa: string | null;
}

const aMinutos = (hora: string) => {
  const [h, m] = hora.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};

// Solape real de rangos, no coincidencia exacta de hora
// Un fin menor o igual que el inicio es del día siguiente (cruza medianoche).
const finMinutos = (inicio: string, fin: string) =>
  aMinutos(fin) > aMinutos(inicio) ? aMinutos(fin) : aMinutos(fin) + 1440;

export const haySolape = (
  inicioA: string,
  finA: string,
  inicioB: string,
  finB: string
) =>
  aMinutos(inicioA) < finMinutos(inicioB, finB) &&
  finMinutos(inicioA, finA) > aMinutos(inicioB);

export const useCitasPendientesPorCliente = (email: string | null) => {
  const [citas, setCitas] = useState<CitaPendienteCliente[]>([]);

  const fetchCitas = async () => {
    if (!email) {
      setCitas([]);
      return;
    }

    const hoyISO = new Date().toISOString().slice(0, 10);

    const { data, error } = await supabase
      .from('vista_citas_detalle')
      .select('id_cita, fecha_cita, hora_cita, hora_fin, nombre_empresa')
      .eq('email_contacto', email)
      .gte('fecha_cita', hoyISO)
      .in('estado_cita', ['pendiente', 'confirmado']);

    if (error) {
      console.error('Error comprobando citas pendientes:', error);
      setCitas([]);
      return;
    }

    setCitas((data ?? []) as CitaPendienteCliente[]);
  };

  useEffect(() => {
    fetchCitas();
  }, [email]);

  return { pending: citas.length > 0, citas, refetch: fetchCitas };
};

export async function obtenerCita(idCita: string) {
  try {
    const { data, error } = await supabase
      .rpc('obtener_cita_publica', { p_id_cita: idCita });
    if (error || !data) return { ok: false, error: 'Cita no encontrada' };
    return { ok: true, cita: data };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita } });
    console.error('Error al obtener cita:', err);
    return { ok: false, error: 'Error interno al obtener la cita' };
  }
}

/* ============================================================
   MOVIMIENTO DE CITA
   El histórico de reprogramación (citas_reprogramaciones) se
   inserta aquí directamente, indicando explícitamente quién
   reprograma ('cliente' | 'trabajador'), porque un mismo
   usuario puede tener varios perfiles y el trigger no puede
   adivinar con certeza desde qué panel se hizo el cambio.
   El dueño de la empresa se trata como 'trabajador'.
============================================================ */

export async function moverCita(
  idCita: string,
  nuevaFecha: string,
  nuevaHora: string,
  nuevaHoraFin: string,
  reprogramadoPor: 'cliente' | 'trabajador'
) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');

    // 1. Leemos la fecha/hora actuales ANTES de actualizar, para
    //    poder registrar correctamente el valor "anterior" en el histórico.
    const { data: citaActual, error: errorLectura } = await supabase
      .from('citas')
      .select('fecha_cita, hora_cita, fecha_fin, desbordamiento')
      .eq('id_cita', idCita)
      .single();

    if (errorLectura || !citaActual) throw errorLectura;

    // Si la cita (no desbordada) termina después de medianoche, fecha_fin es
    // el día siguiente; si no, se limpia para que no quede la de antes.
    let nuevaFechaFin = citaActual.fecha_fin;
    if (!citaActual.desbordamiento) {
      if (nuevaHoraFin && nuevaHoraFin.slice(0, 5) <= nuevaHora.slice(0, 5)) {
        const d = new Date(`${nuevaFecha}T00:00:00`);
        d.setDate(d.getDate() + 1);
        nuevaFechaFin = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else {
        nuevaFechaFin = null;
      }
    }

    // 2. Actualizamos la cita con la nueva fecha/hora.
    const { error: errorUpdate } = await supabase
      .from('citas')
      .update({
        fecha_cita: nuevaFecha,
        hora_cita: nuevaHora.slice(0, 5),
        hora_fin: nuevaHoraFin,
        fecha_fin: nuevaFechaFin,
        usuario_edicion: idUsuario,
      })
      .eq('id_cita', idCita);

    if (errorUpdate) throw errorUpdate;

    // 3. Registramos el histórico de reprogramación con el dato
    //    explícito de quién la hizo.
    const { error: errorHistorico } = await supabase
      .from('citas_reprogramaciones')
      .insert([
        {
          id_cita: idCita,
          fecha_anterior: citaActual.fecha_cita,
          hora_anterior: citaActual.hora_cita,
          fecha_nueva: nuevaFecha,
          hora_nueva: nuevaHora,
          reprogramado_por: reprogramadoPor,
          usuario_creacion: idUsuario,
        },
      ]);

    if (errorHistorico) throw errorHistorico;

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, {
      extra: { idCita, nuevaFecha, nuevaHora, reprogramadoPor },
    });
    console.error('Error al mover la cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   HELPER: obtener id_estado
============================================================ */

export async function obtenerIdEstado(nombreEstado: string) {
  const { data, error } = await supabase
    .from('estados')
    .select('id_estado')
    .eq('estado', nombreEstado)
    .maybeSingle();

  if (error || !data)
    return { ok: false, error: `No se encontró estado "${nombreEstado}"` };

  return { ok: true, id: data.id_estado };
}

/* ============================================================
   REABRIR CITA
============================================================ */

export async function reabrirCita(idCita: string) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');

    const resp = await obtenerIdEstado('confirmado');
    if (!resp.ok) return resp;

    const { error } = await supabase
      .from('citas')
      .update({
        id_estado: resp.id,
        usuario_edicion: idUsuario,
      })
      .eq('id_cita', idCita);

    if (error) throw error;

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita } });
    console.error('Error al reabrir cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   ANULAR CITA
============================================================ */

export async function anularCita(idCita: string, motivo: string) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');

    const resp = await obtenerIdEstado('anulado');
    if (!resp.ok) return resp;

    const { error } = await supabase
      .from('citas')
      .update({
        id_estado: resp.id,
        motivo_anulado: motivo,
        usuario_edicion: idUsuario,
      })
      .eq('id_cita', idCita);

    if (error) throw error;

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita, motivo } });
    console.error('Error al anular cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   CANCELAR CITA
============================================================ */

export async function cancelarCita(idCita: string, motivo: string) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');
    const { data, error } = await supabase.rpc('cancelar_cita_publica', {
      p_id_cita: idCita,
      p_motivo: motivo || null,
      p_usuario: idUsuario || null,
    });
    if (error) throw error;
    if (data === false) {
      return { ok: false, error: 'Solo se pueden cancelar citas confirmadas' };
    }
    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita, motivo } });
    console.error('Error al cancelar cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   COMPLETAR CITA
============================================================ */

export async function completarCita(
  idCita: string,
  tipoPago: string,
  precio: number,
  bonoDisponible?: { id_bono_comprado: string } | null
) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');

    const resp = await obtenerIdEstado('completado');
    if (!resp.ok) return resp;

    const ahora = new Date().toISOString();

    const { error } = await supabase
      .from('citas')
      .update({
        id_estado: resp.id,
        tipo_pago: tipoPago,
        precio: precio,
        fecha_completado: ahora,
        usuario_edicion: idUsuario,
      })
      .eq('id_cita', idCita);

    if (error) throw error;

    if (tipoPago === 'bono' && bonoDisponible) {
      const { error: usoError } = await supabase.from('uso_bono').insert([
        {
          id_bono_comprado: bonoDisponible.id_bono_comprado,
          id_cita: idCita,
          usuario_creacion: idUsuario,
        },
      ]);

      if (usoError) throw usoError;
    }

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita, tipoPago, precio } });
    console.error('Error al completar cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   CREAR CITA
   Usa la RPC crear_cita_publica (SECURITY DEFINER) para ambos
   flujos: anónimo y autenticado. Esto evita los problemas de
   RLS en el RETURNING de citas y en usuario_en_cita.
============================================================ */

export async function crearCita(cita: {
  trabajador: string | null;
  servicio: string;
  telefono?: string;
  email?: string;
  nombre?: string;
  fecha: string;
  fechaFin?: string | null;
  hora: string;
  horaFin: string;
  observaciones?: string;
  num_clientes: number;
  recurso_numero?: number | null;
  // false para una reserva continua que solo cruza la medianoche (fechaFin =
  // día siguiente); por defecto, cualquier fechaFin se trata como desbordada.
  desbordamiento?: boolean;
}) {
  try {
    const idUsuarioSistema = localStorage.getItem('id_usuario');

    const estadoResp = await obtenerIdEstado('confirmado');
    if (!estadoResp.ok) return estadoResp;

    const { data: servicioSel, error: servicioError } = await supabase
      .from('servicios')
      .select('*')
      .eq('id_servicio', cita.servicio)
      .maybeSingle();

    if (servicioError || !servicioSel) {
      return { ok: false, error: 'Servicio no encontrado' };
    }

    let recursoFinal = 1;
    if (servicioSel.permite_grupos) {
      recursoFinal = cita.recurso_numero || 1;
    }

    if (servicioSel.cliente_max > 1) {
      const { data: reservas, error: reservasError } = await supabase
        .from('vista_citas_detalle')
        .select('num_clientes')
        .eq('id_servicio', cita.servicio)
        .eq('fecha_cita', cita.fecha)
        // La vista devuelve la hora como HH:MM, no como HH:MM:SS.
        .eq('hora_cita', cita.hora.slice(0, 5))
        .eq('recurso_numero', recursoFinal)
        .in('estado_cita', ['pendiente', 'confirmado']);

      if (reservasError) {
        return { ok: false, error: 'Error comprobando plazas disponibles' };
      }

      const totalReservado =
        reservas?.reduce((acc, r) => acc + (r.num_clientes || 0), 0) || 0;

      const libres = Math.max(servicioSel.cliente_max - totalReservado, 0);

      if ((cita.num_clientes || 1) > libres) {
        return {
          ok: false,
          error:
            libres === 0
              ? 'Ya no quedan plazas para las ' +
                cita.hora.slice(0, 5) +
                '. Elige otra hora.'
              : 'Solo ' +
                (libres === 1 ? 'queda 1 plaza' : `quedan ${libres} plazas`) +
                ' para las ' +
                cita.hora.slice(0, 5) +
                ' y has pedido ' +
                (cita.num_clientes || 1) +
                '.',
        };
      }
    }

    const precioUnit = servicioSel.precio ?? 0;
    const minutos = (h: string) => {
      const [hh, mm] = h.split(':').map(Number);
      return hh * 60 + mm;
    };
    const diffMin = minutos(cita.horaFin) - minutos(cita.hora);
    const horas = servicioSel.reserva_por_horas
      ? Math.max(1, Math.round((diffMin > 0 ? diffMin : diffMin + 1440) / 60))
      : 1;
    const precioTotal =
      (servicioSel.precio_aplica === 'Por persona'
        ? precioUnit * (cita.num_clientes || 1)
        : precioUnit) * horas;

    const { data: idCita, error } = await supabase.rpc('crear_cita_publica', {
      p_id_servicio: cita.servicio,
      p_nombre: cita.nombre || null,
      p_telefono: cita.telefono || null,
      p_email: cita.email || null,
      p_fecha_cita: cita.fecha,
      p_fecha_fin: cita.fechaFin || null,
      // hora_cita es varchar: se guarda HH:MM, que es como lo devuelven las
      // vistas y con lo que compara el resto de la app.
      p_hora_cita: cita.hora.slice(0, 5),
      p_hora_fin: cita.horaFin,
      p_id_estado: estadoResp.id,
      p_num_clientes: cita.num_clientes || 1,
      p_recurso_numero: recursoFinal,
      p_observaciones: cita.observaciones || null,
      p_precio: precioTotal,
      p_desbordamiento: cita.desbordamiento ?? Boolean(cita.fechaFin),
      p_usuario_creacion: idUsuarioSistema,
      p_id_trabajador: cita.trabajador || null,
    });

    if (error) throw error;

    return { ok: true, id_cita: idCita as string };
  } catch (err: any) {
    Sentry.captureException(err, {
      extra: {
        servicio: cita.servicio,
        fecha: cita.fecha,
        hora: cita.hora,
        num_clientes: cita.num_clientes,
      },
    });
    console.error('Error al crear cita:', err);

    // Las excepciones de aforo del trigger si son utiles para el cliente; el
    // resto de P0001 no dicen nada que pueda accionar.
    const esAforo = /plazas/i.test(err?.message ?? '');
    const mensaje =
      err?.code === 'P0001'
        ? esAforo
          ? 'Alguien ha reservado esas plazas mientras rellenabas el formulario. Recarga y elige otra hora.'
          : 'No se ha podido completar la reserva. Contacta con el negocio.'
        : err?.message || 'Error al registrar la cita';

    return { ok: false, error: mensaje };
  }
}

/* ============================================================
   VALORAR CITA
   Usa la RPC valorar_cita_publica (SECURITY DEFINER) porque la
   página de valoración es pública: el cliente llega desde el
   enlace del correo sin sesión iniciada, y las policies de
   valoraciones solo cubren el rol authenticated.
============================================================ */

export async function valorarCita(
  idCita: string,
  puntuacion: number,
  comentario?: string | null
) {
  try {
    if (!puntuacion || puntuacion < 1 || puntuacion > 5) {
      return { ok: false, error: 'La puntuación debe estar entre 1 y 5' };
    }

    const { error } = await supabase.rpc('valorar_cita_publica', {
      p_id_cita: idCita,
      p_puntuacion: puntuacion,
      p_comentario: comentario?.trim() || null,
    });

    if (error) throw error;

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita, puntuacion } });
    console.error('Error al valorar cita:', err);
    return { ok: false, error: err };
  }
}

/* ============================================================
   EDITAR CITA
============================================================ */

export async function editarCita(
  idCita: string,
  campos: {
    id_servicio?: string;
    nombre?: string;
    telefono?: string;
    email?: string;
    hora_fin?: string;
    tipo_pago?: string;
  }
) {
  try {
    const idUsuario = localStorage.getItem('id_usuario');

    const { error } = await supabase
      .from('citas')
      .update({
        ...campos,
        fecha_edicion: new Date().toISOString(),
        usuario_edicion: idUsuario,
      })
      .eq('id_cita', idCita);

    if (error) throw error;

    return { ok: true };
  } catch (err) {
    Sentry.captureException(err, { extra: { idCita, campos } });
    console.error('Error al editar cita:', err);
    return { ok: false, error: err };
  }
}